import React, { useEffect, useRef } from 'react';

function AnimationHero() {
    const canvasRef = useRef(null);
    const animationFrameRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let blocks = [];
        let buildings = [];
        let morphProgress = 0;
        let isText = true;
        let transitionDelay = 0;

        const TRANSITION_DURATION = 120;
        const HOLD_DURATION = 90;

        const resizeCanvas = () => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width || window.innerWidth;
            canvas.height = rect.height || window.innerHeight;

            if (canvas.width < 100) canvas.width = window.innerWidth;
            if (canvas.height < 100) canvas.height = window.innerHeight;

            initializeAnimation();
        };

        const initializeAnimation = () => {
            const text = 'WarpMill';
            blocks = [];
            buildings = [];

            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');

            const maxTextWidth = canvas.width * 0.7;
            let fontSize = 140;
            tempCtx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
            let textWidth = tempCtx.measureText(text).width;

            while (textWidth > maxTextWidth && fontSize > 30) {
                fontSize -= 3;
                tempCtx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
                textWidth = tempCtx.measureText(text).width;
            }

            const textHeight = fontSize * 1.1;
            tempCanvas.width = textWidth + 60;
            tempCanvas.height = textHeight + 60;
            tempCtx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
            tempCtx.fillStyle = '#FFFFFF';
            tempCtx.textBaseline = 'top';
            tempCtx.fillText(text, 30, 30);

            const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            const blockSize = 4;

            for (let y = 0; y < tempCanvas.height; y += blockSize) {
                for (let x = 0; x < tempCanvas.width; x += blockSize) {
                    const index = (y * tempCanvas.width + x) * 4;
                    if (imageData.data[index + 3] > 128) {
                        blocks.push({
                            x: x,
                            y: y,
                            size: blockSize,
                            depth: Math.random() * 0.5 + 0.5
                        });
                    }
                }
            }

            const buildingCount = Math.floor(textWidth / 35);
            const buildingWidth = textWidth / buildingCount;
            
            // Increase building height on mobile
            const isMobile = canvas.width <= 768;
            const maxBuildingHeight = isMobile ? textHeight * 1.5 : textHeight;
            const minBuildingHeight = isMobile ? textHeight * 0.5 : textHeight * 0.3;

            for (let i = 0; i < buildingCount; i++) {
                const height = Math.random() * (maxBuildingHeight - minBuildingHeight) + minBuildingHeight;
                const buildingType = Math.random();
                
                // Calculate baseline for bottom alignment
                const baselineY = maxBuildingHeight;
                const topY = baselineY - height;

                buildings.push({
                    x: i * buildingWidth,
                    y: topY,
                    width: buildingWidth * 0.95,
                    height: height,
                    depth: Math.random() * 0.3 + 0.7,
                    windows: Math.floor(height / 12),
                    windowCols: Math.floor(buildingWidth * 0.95 / 8),
                    type: buildingType > 0.7 ? 'skyscraper' : buildingType > 0.4 ? 'office' : 'apartment',
                    roofType: Math.random() > 0.5 ? 'flat' : 'peaked',
                    hasAntenna: Math.random() > 0.7,
                    lightPattern: Math.random()
                });
            }

            const textCenterX = canvas.width / 2;
            const textCenterY = canvas.height * 0.45;

            blocks.forEach(block => {
                block.textX = textCenterX - textWidth / 2 + block.x - 30;
                block.textY = textCenterY - textHeight / 2 + block.y - 30;
            });

            // Position buildings so they align at the bottom with text baseline
            const buildingAreaHeight = maxBuildingHeight;
            buildings.forEach(building => {
                building.textX = textCenterX - textWidth / 2 + building.x;
                // Align buildings at the bottom - text baseline is at textCenterY + textHeight/2
                building.textY = textCenterY + textHeight / 2 - buildingAreaHeight + building.y;
            });
        };

        const drawBackground = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#0A0A1A';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        };

        const easeInOutCubic = (t) => {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        const drawMorph = () => {
            const progress = easeInOutCubic(morphProgress);

            if (isText) {
                if (progress < 0.2) {
                    const text = 'WarpMill';
                    const maxTextWidth = canvas.width * 0.7;
                    let fontSize = 140;
                    ctx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
                    let textWidth = ctx.measureText(text).width;

                    while (textWidth > maxTextWidth && fontSize > 30) {
                        fontSize -= 3;
                        ctx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
                        textWidth = ctx.measureText(text).width;
                    }

                    ctx.fillStyle = '#FFFFFF';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, canvas.width / 2, canvas.height * 0.45);
                } else {
                    blocks.forEach((block, i) => {
                        const building = buildings[i % buildings.length];
                        const buildingBlockIndex = Math.floor(i / buildings.length);
                        const maxBlockCols = Math.ceil(building.width / 6);
                        const maxBlockRows = Math.ceil(building.height / 6);
                        const blockRow = Math.floor(buildingBlockIndex / maxBlockCols);
                        const blockCol = buildingBlockIndex % maxBlockCols;

                        // Skip blocks that would be outside building bounds
                        if (blockRow >= maxBlockRows || blockCol >= maxBlockCols) {
                            return;
                        }

                        const targetX = building.textX + blockCol * 6;
                        const targetY = building.textY + blockRow * 6;
                        
                        // Skip blocks that are below the building's bottom edge
                        if (targetY >= building.textY + building.height || targetY < building.textY) {
                            return;
                        }
                        
                        // Skip blocks outside building horizontal bounds
                        if (targetX < building.textX || targetX >= building.textX + building.width) {
                            return;
                        }
                        
                        const explosionProgress = (progress - 0.2) / 0.8;

                        let currentX, currentY;
                        if (explosionProgress < 0.3) {
                            const explosionForce = (0.3 - explosionProgress) * 250;
                            const angle = (i * 137.5) * (Math.PI / 180);
                            currentX = block.textX + Math.cos(angle) * explosionForce;
                            currentY = block.textY + Math.sin(angle) * explosionForce;
                        } else {
                            const convergeProgress = (explosionProgress - 0.3) / 0.7;
                            const scatterX = block.textX + Math.cos((i * 137.5) * (Math.PI / 180)) * 75;
                            const scatterY = block.textY + Math.sin((i * 137.5) * (Math.PI / 180)) * 75;

                            currentX = scatterX + (targetX - scatterX) * convergeProgress;
                            currentY = scatterY + (targetY - scatterY) * convergeProgress;
                        }
                        
                        // Skip drawing if current position is below building bottom or outside bounds
                        if (currentY > building.textY + building.height || currentY < building.textY) {
                            return;
                        }
                        if (currentX < building.textX || currentX > building.textX + building.width) {
                            return;
                        }

                        const rotation = explosionProgress * Math.PI * 2;
                        ctx.save();
                        ctx.translate(currentX + block.size / 2, currentY + block.size / 2);
                        ctx.rotate(rotation);
                        ctx.fillStyle = '#FFFFFF';
                        ctx.globalAlpha = 1;
                        ctx.fillRect(-block.size / 2, -block.size / 2, block.size, block.size);
                        ctx.restore();
                    });
                }

                if (progress > 0.6) {
                    const buildingAlpha = (progress - 0.6) * 2.5;
                    buildings.forEach((building, buildingIndex) => {
                        // Building base structure with realistic materials
                        const buildingGradient = ctx.createLinearGradient(
                            building.textX, building.textY,
                            building.textX, building.textY + building.height
                        );
                        
                        if (building.type === 'skyscraper') {
                            // Glass and steel skyscraper with reflective quality
                            buildingGradient.addColorStop(0, `rgba(45, 55, 75, ${buildingAlpha})`);
                            buildingGradient.addColorStop(0.3, `rgba(55, 65, 85, ${buildingAlpha})`);
                            buildingGradient.addColorStop(0.7, `rgba(40, 50, 70, ${buildingAlpha})`);
                            buildingGradient.addColorStop(1, `rgba(30, 40, 60, ${buildingAlpha})`);
                        } else if (building.type === 'office') {
                            // Concrete office building
                            buildingGradient.addColorStop(0, `rgba(85, 90, 100, ${buildingAlpha})`);
                            buildingGradient.addColorStop(0.5, `rgba(75, 80, 90, ${buildingAlpha})`);
                            buildingGradient.addColorStop(1, `rgba(65, 70, 80, ${buildingAlpha})`);
                        } else {
                            // Brick apartment building
                            buildingGradient.addColorStop(0, `rgba(95, 75, 65, ${buildingAlpha})`);
                            buildingGradient.addColorStop(0.5, `rgba(85, 65, 55, ${buildingAlpha})`);
                            buildingGradient.addColorStop(1, `rgba(75, 55, 45, ${buildingAlpha})`);
                        }
                        
                        ctx.fillStyle = buildingGradient;
                        ctx.fillRect(building.textX, building.textY, building.width, building.height);
                        
                        // Add 3D depth effect - side panels
                        if (progress > 0.65) {
                            const depthOffset = 3;
                            const sideAlpha = buildingAlpha * 0.6;
                            
                            // Right side shadow for depth
                            ctx.fillStyle = `rgba(0, 0, 0, ${sideAlpha * 0.3})`;
                            ctx.beginPath();
                            ctx.moveTo(building.textX + building.width, building.textY);
                            ctx.lineTo(building.textX + building.width + depthOffset, building.textY - depthOffset);
                            ctx.lineTo(building.textX + building.width + depthOffset, building.textY + building.height - depthOffset);
                            ctx.lineTo(building.textX + building.width, building.textY + building.height);
                            ctx.fill();
                            
                            // Top edge highlight
                            ctx.fillStyle = `rgba(255, 255, 255, ${sideAlpha * 0.2})`;
                            ctx.beginPath();
                            ctx.moveTo(building.textX, building.textY);
                            ctx.lineTo(building.textX + depthOffset, building.textY - depthOffset);
                            ctx.lineTo(building.textX + building.width + depthOffset, building.textY - depthOffset);
                            ctx.lineTo(building.textX + building.width, building.textY);
                            ctx.fill();
                        }
                        
                        // Building texture and architectural details
                        if (progress > 0.65) {
                            // Vertical structural lines (dividing building sections)
                            ctx.strokeStyle = `rgba(0, 0, 0, ${buildingAlpha * 0.25})`;
                            ctx.lineWidth = 1;
                            for (let i = 1; i < 4; i++) {
                                const lineX = building.textX + (building.width / 4) * i;
                                ctx.beginPath();
                                ctx.moveTo(lineX, building.textY);
                                ctx.lineTo(lineX, building.textY + building.height);
                                ctx.stroke();
                            }
                            
                            // Horizontal floor lines (dividing floors)
                            ctx.strokeStyle = `rgba(0, 0, 0, ${buildingAlpha * 0.15})`;
                            ctx.lineWidth = 0.5;
                            for (let floor = 1; floor < building.windows; floor++) {
                                const lineY = building.textY + floor * 12;
                                ctx.beginPath();
                                ctx.moveTo(building.textX, lineY);
                                ctx.lineTo(building.textX + building.width, lineY);
                                ctx.stroke();
                            }
                            
                            // Horizontal section dividers (every few floors for depth)
                            ctx.strokeStyle = `rgba(0, 0, 0, ${buildingAlpha * 0.3})`;
                            ctx.lineWidth = 1.2;
                            const sectionHeight = building.height / 3;
                            for (let section = 1; section < 3; section++) {
                                const sectionY = building.textY + section * sectionHeight;
                                ctx.beginPath();
                                ctx.moveTo(building.textX, sectionY);
                                ctx.lineTo(building.textX + building.width, sectionY);
                                ctx.stroke();
                            }
                            
                            // Building edge highlights (left edge)
                            ctx.strokeStyle = `rgba(255, 255, 255, ${buildingAlpha * 0.2})`;
                            ctx.lineWidth = 1.5;
                            ctx.beginPath();
                            ctx.moveTo(building.textX, building.textY);
                            ctx.lineTo(building.textX, building.textY + building.height);
                            ctx.stroke();
                            
                            // Right edge shadow for depth
                            ctx.strokeStyle = `rgba(0, 0, 0, ${buildingAlpha * 0.25})`;
                            ctx.lineWidth = 1.2;
                            ctx.beginPath();
                            ctx.moveTo(building.textX + building.width, building.textY);
                            ctx.lineTo(building.textX + building.width, building.textY + building.height);
                            ctx.stroke();
                        }
                        
                        // Optimized windows - lighter rendering with colorful variations
                        if (progress > 0.7) {
                            const windowAlpha = (progress - 0.7) * 3.33;
                            
                            // Reduce window density by 50% (skip every other window)
                            for (let row = 0; row < building.windows; row += 1.5) {
                                for (let col = 0; col < building.windowCols; col += 1.5) {
                                    const windowX = building.textX + Math.floor(col) * 8 + 2;
                                    const windowY = building.textY + Math.floor(row) * 12 + 4;
                                    
                                    // Simplified lighting pattern (no time-based calculations)
                                    const randomFactor = Math.sin((buildingIndex + Math.floor(row) * 10 + Math.floor(col)) * 123.456);
                                    const isLit = randomFactor > -0.2;
                                    
                                    // Colorful window rendering with variations
                                    if (isLit) {
                                        // Color variation based on position for visual interest
                                        const colorSeed = (buildingIndex + Math.floor(row) + Math.floor(col)) % 5;
                                        let windowColor;
                                        
                                        switch (colorSeed) {
                                            case 0:
                                                // Warm yellow/white (office)
                                                windowColor = `rgba(255, 240, 200, ${windowAlpha * 0.8})`;
                                                break;
                                            case 1:
                                                // Warm orange (residential)
                                                windowColor = `rgba(255, 200, 150, ${windowAlpha * 0.75})`;
                                                break;
                                            case 2:
                                                // Cool blue (tech office)
                                                windowColor = `rgba(150, 200, 255, ${windowAlpha * 0.7})`;
                                                break;
                                            case 3:
                                                // Purple/cyan (creative)
                                                windowColor = `rgba(200, 150, 255, ${windowAlpha * 0.7})`;
                                                break;
                                            default:
                                                // Teal/cyan accent
                                                windowColor = `rgba(100, 240, 255, ${windowAlpha * 0.7})`;
                                                break;
                                        }
                                        ctx.fillStyle = windowColor;
                                    } else {
                                        // Dark window with slight color variation
                                        const darkSeed = (buildingIndex + Math.floor(col)) % 3;
                                        if (darkSeed === 0) {
                                            ctx.fillStyle = `rgba(20, 30, 45, ${windowAlpha * 0.85})`;
                                        } else if (darkSeed === 1) {
                                            ctx.fillStyle = `rgba(15, 25, 40, ${windowAlpha * 0.85})`;
                                        } else {
                                            ctx.fillStyle = `rgba(25, 35, 50, ${windowAlpha * 0.85})`;
                                        }
                                    }
                                    ctx.fillRect(windowX, windowY, 5, 8);
                                    
                                    // Simple frame (single stroke, no mullions)
                                    ctx.strokeStyle = `rgba(40, 40, 50, ${windowAlpha * 0.7})`;
                                    ctx.lineWidth = 0.6;
                                    ctx.strokeRect(windowX, windowY, 5, 8);
                                }
                            }
                        }
                        
                        // Enhanced rooftop details with dividing lines
                        if (progress > 0.8) {
                            const roofAlpha = (progress - 0.8) * 5;
                            
                            if (building.roofType === 'peaked') {
                                // Peaked roof with realistic shading
                                const roofGradient = ctx.createLinearGradient(
                                    building.textX, building.textY - 8,
                                    building.textX + building.width, building.textY - 8
                                );
                                roofGradient.addColorStop(0, `rgba(60, 60, 70, ${buildingAlpha})`);
                                roofGradient.addColorStop(0.5, `rgba(80, 80, 90, ${buildingAlpha})`);
                                roofGradient.addColorStop(1, `rgba(50, 50, 60, ${buildingAlpha})`);
                                
                                ctx.fillStyle = roofGradient;
                                ctx.beginPath();
                                ctx.moveTo(building.textX, building.textY);
                                ctx.lineTo(building.textX + building.width/2, building.textY - 15);
                                ctx.lineTo(building.textX + building.width, building.textY);
                                ctx.fill();
                                
                                // Roof edge shadow
                                ctx.strokeStyle = `rgba(30, 30, 40, ${buildingAlpha})`;
                                ctx.lineWidth = 1.5;
                                ctx.stroke();
                                
                                // Roof highlight
                                ctx.strokeStyle = `rgba(255, 255, 255, ${buildingAlpha * 0.2})`;
                                ctx.lineWidth = 1;
                                ctx.beginPath();
                                ctx.moveTo(building.textX, building.textY);
                                ctx.lineTo(building.textX + building.width/2, building.textY - 15);
                                ctx.stroke();
                            } else {
                                // Flat roof with HVAC units and equipment
                                // Main roof surface
                                ctx.fillStyle = `rgba(70, 70, 80, ${buildingAlpha * 0.9})`;
                                ctx.fillRect(building.textX, building.textY - 2, building.width, 2);
                                
                                // HVAC units
                                ctx.fillStyle = `rgba(85, 85, 95, ${buildingAlpha * 0.8})`;
                                ctx.fillRect(building.textX + building.width * 0.15, building.textY - 5, building.width * 0.25, 3);
                                ctx.fillRect(building.textX + building.width * 0.6, building.textY - 6, building.width * 0.2, 4);
                                
                                // HVAC shadows
                                ctx.fillStyle = `rgba(0, 0, 0, ${buildingAlpha * 0.3})`;
                                ctx.fillRect(building.textX + building.width * 0.15, building.textY - 2, building.width * 0.25, 1);
                                ctx.fillRect(building.textX + building.width * 0.6, building.textY - 2, building.width * 0.2, 1);
                                
                                // Water tower (for some buildings)
                                if (building.hasAntenna && Math.random() > 0.6) {
                                    ctx.fillStyle = `rgba(70, 75, 80, ${buildingAlpha})`;
                                    ctx.fillRect(building.textX + building.width * 0.8, building.textY - 10, building.width * 0.15, 8);
                                    // Tank top
                                    ctx.beginPath();
                                    ctx.ellipse(building.textX + building.width * 0.875, building.textY - 10, building.width * 0.075, 2, 0, 0, Math.PI * 2);
                                    ctx.fill();
                                }
                            }
                        }
                        
                        // Enhanced antenna and architectural details
                        if (progress > 0.85) {
                            const detailAlpha = (progress - 0.85) * 6.67;
                            
                            if (building.hasAntenna) {
                                // Main antenna mast with realistic structure
                                const antennaX = building.textX + building.width/2;
                                const antennaBase = building.textY;
                                const antennaHeight = 25;
                                
                                // Antenna shadow/depth
                                ctx.strokeStyle = `rgba(100, 100, 110, ${buildingAlpha * 0.6})`;
                                ctx.lineWidth = 2;
                                ctx.beginPath();
                                ctx.moveTo(antennaX + 1, antennaBase);
                                ctx.lineTo(antennaX + 1, antennaBase - antennaHeight);
                                ctx.stroke();
                                
                                // Main antenna
                                ctx.strokeStyle = `rgba(180, 180, 190, ${buildingAlpha})`; 
                                ctx.lineWidth = 1.8;
                                ctx.beginPath();
                                ctx.moveTo(antennaX, antennaBase);
                                ctx.lineTo(antennaX, antennaBase - antennaHeight);
                                ctx.stroke();
                                
                                // Antenna crossbars with realistic positioning
                                ctx.lineWidth = 1;
                                for (let i = 1; i <= 4; i++) {
                                    const barY = antennaBase - (i * 6);
                                    const barWidth = 10 - (i * 1.5);
                                    
                                    // Crossbar shadow
                                    ctx.strokeStyle = `rgba(100, 100, 110, ${buildingAlpha * 0.5})`;
                                    ctx.beginPath();
                                    ctx.moveTo(antennaX - barWidth/2, barY + 0.5);
                                    ctx.lineTo(antennaX + barWidth/2, barY + 0.5);
                                    ctx.stroke();
                                    
                                    // Main crossbar
                                    ctx.strokeStyle = `rgba(180, 180, 190, ${buildingAlpha})`;
                                    ctx.beginPath();
                                    ctx.moveTo(antennaX - barWidth/2, barY);
                                    ctx.lineTo(antennaX + barWidth/2, barY);
                                    ctx.stroke();
                                    
                                    // Support wires
                                    ctx.strokeStyle = `rgba(150, 150, 160, ${buildingAlpha * 0.4})`;
                                    ctx.lineWidth = 0.3;
                                    ctx.beginPath();
                                    ctx.moveTo(antennaX - barWidth/2, barY);
                                    ctx.lineTo(antennaX, antennaBase);
                                    ctx.moveTo(antennaX + barWidth/2, barY);
                                    ctx.lineTo(antennaX, antennaBase);
                                    ctx.stroke();
                                }
                                
                                // Colorful blinking warning light with realistic animation
                                const blinkCycle = Math.sin(Date.now() * 0.005);
                                if (blinkCycle > 0) {
                                    // Color variation for antenna lights
                                    const lightColorSeed = buildingIndex % 4;
                                    let lightColor, lightGlowColor;
                                    
                                    switch (lightColorSeed) {
                                        case 0:
                                            lightColor = `rgba(255, 100, 100, ${buildingAlpha})`; // Red
                                            lightGlowColor = 'rgba(255, 100, 100, 0)';
                                            break;
                                        case 1:
                                            lightColor = `rgba(100, 255, 150, ${buildingAlpha})`; // Green
                                            lightGlowColor = 'rgba(100, 255, 150, 0)';
                                            break;
                                        case 2:
                                            lightColor = `rgba(100, 200, 255, ${buildingAlpha})`; // Blue
                                            lightGlowColor = 'rgba(100, 200, 255, 0)';
                                            break;
                                        default:
                                            lightColor = `rgba(255, 200, 100, ${buildingAlpha})`; // Orange
                                            lightGlowColor = 'rgba(255, 200, 100, 0)';
                                            break;
                                    }
                                    
                                    // Light glow
                                    const lightGradient = ctx.createRadialGradient(
                                        antennaX, antennaBase - antennaHeight - 2, 0,
                                        antennaX, antennaBase - antennaHeight - 2, 5
                                    );
                                    // Extract RGB from lightColor and apply blink alpha
                                    const rgbMatch = lightColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
                                    if (rgbMatch) {
                                        const r = rgbMatch[1];
                                        const g = rgbMatch[2];
                                        const b = rgbMatch[3];
                                        lightGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${buildingAlpha * blinkCycle})`);
                                    }
                                    lightGradient.addColorStop(1, lightGlowColor);
                                    ctx.fillStyle = lightGradient;
                                    ctx.beginPath();
                                    ctx.arc(antennaX, antennaBase - antennaHeight - 2, 5, 0, Math.PI * 2);
                                    ctx.fill();
                                    
                                    // Light core
                                    ctx.fillStyle = lightColor;
                                    ctx.beginPath();
                                    ctx.arc(antennaX, antennaBase - antennaHeight - 2, 1.8, 0, Math.PI * 2);
                                    ctx.fill();
                                }
                            }
                            
                            // Building ledges and architectural details
                            if (building.type === 'skyscraper') {
                                // Modern architectural fins
                                ctx.fillStyle = `rgba(60, 70, 85, ${buildingAlpha * 0.7})`;
                                for (let i = 0; i < 3; i++) {
                                    const finX = building.textX + building.width * (0.2 + i * 0.3);
                                    ctx.fillRect(finX, building.textY, 1, building.height);
                                }
                            }
                            
                            // Building signage on top floors
                            if (progress > 0.9 && building.height > 60) {
                                const signY = building.textY + building.height * 0.1;
                                ctx.fillStyle = `rgba(255, 255, 255, ${buildingAlpha * 0.3})`;
                                ctx.fillRect(building.textX + building.width * 0.3, signY, building.width * 0.4, 3);
                            }
                        }
                    });
                }
            } else {
                if (progress < 0.8) {
                    buildings.forEach((building, i) => {
                        const relatedBlocks = blocks.filter((_, idx) => idx % buildings.length === i);
                        
                        relatedBlocks.forEach((block, blockIdx) => {
                            const buildingBlockIndex = Math.floor((i * relatedBlocks.length + blockIdx) / buildings.length);
                            const maxBlockCols = Math.ceil(building.width / 6);
                            const maxBlockRows = Math.ceil(building.height / 6);
                            const blockRow = Math.floor(buildingBlockIndex / maxBlockCols);
                            const blockCol = buildingBlockIndex % maxBlockCols;
                            
                            // Skip blocks that would be outside building bounds
                            if (blockRow >= maxBlockRows || blockCol >= maxBlockCols) {
                                return;
                            }
                            
                            const startX = building.textX + blockCol * 6;
                            const startY = building.textY + blockRow * 6;
                            
                            // Skip blocks that are below the building's bottom edge or outside bounds
                            if (startY >= building.textY + building.height || startY < building.textY) {
                                return;
                            }
                            if (startX < building.textX || startX >= building.textX + building.width) {
                                return;
                            }
                            
                            let currentX, currentY;
                            if (progress < 0.3) {
                                const explosionForce = progress * 150;
                                const angle = (blockIdx * 137.5) * (Math.PI / 180);
                                currentX = startX + Math.cos(angle) * explosionForce;
                                currentY = startY + Math.sin(angle) * explosionForce;
                            } else {
                                const convergeProgress = (progress - 0.3) / 0.5;
                                const scatterX = startX + Math.cos((blockIdx * 137.5) * (Math.PI / 180)) * 45;
                                const scatterY = startY + Math.sin((blockIdx * 137.5) * (Math.PI / 180)) * 45;
                                
                                currentX = scatterX + (block.textX - scatterX) * convergeProgress;
                                currentY = scatterY + (block.textY - scatterY) * convergeProgress;
                            }
                            
                            // Skip drawing if current position is below building bottom or outside bounds
                            if (currentY > building.textY + building.height || currentY < building.textY) {
                                return;
                            }
                            if (currentX < building.textX || currentX > building.textX + building.width) {
                                return;
                            }
                            
                            const rotation = progress * Math.PI * 2;
                            ctx.save();
                            ctx.translate(currentX + block.size/2, currentY + block.size/2);
                            ctx.rotate(-rotation);
                            ctx.fillStyle = '#FFFFFF';
                            ctx.fillRect(-block.size/2, -block.size/2, block.size, block.size);
                            ctx.restore();
                        });
                    });
                } else {
                    const text = 'WarpMill';
                    const maxTextWidth = canvas.width * 0.7;
                    let fontSize = 140;
                    ctx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
                    let textWidth = ctx.measureText(text).width;
                    
                    while (textWidth > maxTextWidth && fontSize > 30) {
                        fontSize -= 3;
                        ctx.font = `900 ${fontSize}px 'Arial Black', Arial, sans-serif`;
                        textWidth = ctx.measureText(text).width;
                    }
                    
                    ctx.fillStyle = `rgba(255, 255, 255, ${(progress - 0.8) * 5})`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, canvas.width / 2, canvas.height * 0.45);
                }
            }

            ctx.globalAlpha = 1;
        };

        const animate = () => {
            drawBackground();
            drawMorph();

            if (transitionDelay > 0) {
                transitionDelay--;
            } else {
                if (morphProgress < 1) {
                    morphProgress += 1 / TRANSITION_DURATION;
                    if (morphProgress >= 1) {
                        morphProgress = 1;
                        transitionDelay = HOLD_DURATION;
                    }
                } else {
                    isText = !isText;
                    morphProgress = 0;
                }
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        const init = () => {
            resizeCanvas();
            animate();
        };

        window.addEventListener('resize', resizeCanvas);

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <section className="animation-hero">
            <canvas id="hero-canvas" ref={canvasRef}></canvas>
            <div className="tagline-overlay">YOUR PARTNER IN HOSPITALITY</div>
        </section>
    );
}

export default AnimationHero;

